import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  TextField,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import { Tooltip } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import { CashTransactionsGet, CashTransactionsExport } from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Description as FileIcon } from "@mui/icons-material";

const LedgerTable = () => {
  // ===== Existing States =====
  const [expandedRow, setExpandedRow] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [ledgerData, setLedgerData] = useState([]);
  const [summary, setSummary] = useState({
    total_debit: 0,
    total_credit: 0,
    balance: 0,
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [excelLoading, setExcelLoading] = useState(false);

  // 📅 Date Range States
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);

  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  // ✅ Temporary state for custom date inputs (before applying)
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });

  // 🆕 Search State for Ledger Name
  const [searchLedgerName, setSearchLedgerName] = useState("");
  const [tempSearchLedgerName, setTempSearchLedgerName] = useState("");

  const [filters, setFilters] = useState({
    date_range: "today",
    ledger_name:"",
  });

  // 📅 Date Display Helper Functions
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const cleaned = dateTimeString.replace(" ", "T");
    const datePart = cleaned.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  // 🆕 Get actual date values for API
  const getDateRangeValues = (rangeType) => {
    const now = new Date();
    const formatAPIDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    switch (rangeType) {
      case "today":
        return { from: formatAPIDate(now), to: formatAPIDate(now) };
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: formatAPIDate(yesterday), to: formatAPIDate(yesterday) };
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return { from: formatAPIDate(weekStart), to: formatAPIDate(now) };
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return { from: formatAPIDate(prevWeekStart), to: formatAPIDate(prevWeekEnd) };
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: formatAPIDate(monthStart), to: formatAPIDate(now) };
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return { from: formatAPIDate(yearStart), to: formatAPIDate(now) };
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return { from: formatAPIDate(fyStart), to: formatAPIDate(now) };
      default:
        return { from: "", to: "" };
    }
  };

  // 🔄 Reset Filters
  const handleReset = () => {
    setFilters({
      date_range: "today",
      ledger_name:"",
    });
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
    setSearchLedgerName("");
    setTempSearchLedgerName("");
  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  // 🆕 Handle Search Ledger Name
  const handleSearchLedgerName = () => {
    setSearchLedgerName(tempSearchLedgerName);
  };

  // 🆕 Handle Clear Search
  const handleClearSearch = () => {
    setSearchLedgerName("");
    setTempSearchLedgerName("");
  };

  // ===== Fetch Ledger with Date Range and Search =====
  const fetchLedger = async (pageNum = 1, reset = false) => {
    setLoading(true);
    const params = {};

    // 🆕 Handle date range
    if (filters.date_range === "custom" && customDateRange.from && customDateRange.to) {
      params.from_date = customDateRange.from;
      params.to_date = customDateRange.to;
    } else if (filters.date_range !== "custom") {
      const dates = getDateRangeValues(filters.date_range);
      params.from_date = dates.from;
      params.to_date = dates.to;
    }

    // 🆕 Add search parameter
    if (searchLedgerName.trim()) {
      params.ledger_name = searchLedgerName.trim();
    }

    try {
      const data = await CashTransactionsGet(params, pageNum);
      if (reset || pageNum === 1) {
        if (data) {
          let runningBalance = 0;
          const entriesWithBalance = (Array.isArray(data.entries) ? data.entries : []).map((entry) => {
            runningBalance += (entry.debit || 0) - (entry.credit || 0);
            return { ...entry, balance: runningBalance };
          });

          setLedgerData(entriesWithBalance);
          setSummary(data.summary || { total_debit: 0, total_credit: 0, balance: 0 });
        }
      } else {
        setLedgerData((prevData) => [...prevData, ...(Array.isArray(data.entries) ? data.entries : [])]);
      }
      setHasMore(data.next !== null);
      setPage(pageNum);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load ledger data");
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // ===== useEffect - Auto fetch on date range or search change =====
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchLedger(1, true);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange, searchLedgerName]);

  // ===== Handlers =====
  const fetchMoreData = () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchLedger(nextPage);
  };

  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const exportFilters = {
        date_range: filters.date_range,
      };

      if (filters.date_range === "custom" && customDateRange.from && customDateRange.to) {
        exportFilters.from_date = customDateRange.from;
        exportFilters.to_date = customDateRange.to;
      } else if (filters.date_range !== "custom") {
        const dates = getDateRangeValues(filters.date_range);
        exportFilters.from_date = dates.from;
        exportFilters.to_date = dates.to;
      }

      // 🆕 Add search parameter to export
      if (searchLedgerName.trim()) {
        exportFilters.ledger_name = searchLedgerName.trim();
      }

      const blob = await CashTransactionsExport(exportFilters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cash_Transactions_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Excel downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel');
    } finally {
      setExcelLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);

  return (
    <Box p={2} sx={{ mt: -3, pt: 0, mb: 2 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ Updated Filter Section with Search */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* 🆕 Ledger Name Search Field */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Ledger Name"
            placeholder="Search by ledger name..."
            value={tempSearchLedgerName}
            onChange={(e) => setTempSearchLedgerName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchLedgerName();
              }
            }}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton 
                  size="small" 
                  edge="end"
                  onClick={handleSearchLedgerName}
                  sx={{ mr: -1 }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiInputBase-root": {
                height: 35,
                fontSize: "0.7rem",
                borderRadius: "0px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
              },
            }}
            fullWidth
          />
        </Grid>

        {/* Date Range Selector */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Date Range"
            value={
              filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                : filters.date_range === "today"
                  ? "Today"
                  : filters.date_range === "yesterday"
                    ? "Yesterday"
                    : filters.date_range === "this_week"
                      ? "This Week"
                      : filters.date_range === "previous_week"
                        ? "Previous Week"
                        : filters.date_range === "this_month"
                          ? "This Month"
                          : filters.date_range === "current_year"
                            ? "Current Year"
                            : filters.date_range === "financial_year"
                              ? "Financial Year"
                              : "Select Date Range"
            }
            onClick={(e) => setDateMenuAnchor(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              width: "100%",
              "& .MuiInputBase-root": {
                height: 35,
                fontSize: "0.7rem",
                borderRadius: "0px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
              },
            }}
            fullWidth
          />
        </Grid>

        {/* Reset Button */}
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              height: "35px",
              whiteSpace: "nowrap",
              borderRadius: "0",
              fontSize: "0.7rem",
            }}
            fullWidth
          >
            Reset
          </Button>
        </Grid>

        {/* 🆕 Clear Search Button (only show when search is active) */}
        {searchLedgerName && (
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearSearch}
              sx={{
                height: "35px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Clear
            </Button>
          </Grid>
        )}

        {/* Excel Export Button */}
        <Grid item xs={12} sm={6} md={searchLedgerName ? 0.5 : 2}>
          <Tooltip title={excelLoading ? "Exporting..." : "Download Excel"} arrow>
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExcelExport}
                disabled={excelLoading}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* ✅ Date Range Menu (unchanged) */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: { width: 350, maxHeight: 500 },
        }}
      >
        {/* Predefined Date Ranges */}
        <MenuItem
          onClick={() => {
            setFilters((prev) => ({ ...prev, date_range: "today" }));
            setCustomDateRange({ from: "", to: "" });
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2">Today</Typography>
            <Typography variant="caption" color="text.secondary">
              {getDateRangeDisplay("today")}
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setFilters((prev) => ({ ...prev, date_range: "yesterday" }));
            setCustomDateRange({ from: "", to: "" });
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2">Yesterday</Typography>
            <Typography variant="caption" color="text.secondary">
              {getDateRangeDisplay("yesterday")}
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setFilters((prev) => ({ ...prev, date_range: "this_week" }));
            setCustomDateRange({ from: "", to: "" });
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2">This Week</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {getDateRangeDisplay("this_week")}
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setFilters((prev) => ({ ...prev, date_range: "previous_week" }));
            setCustomDateRange({ from: "", to: "" });
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2">Previous Week</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {getDateRangeDisplay("previous_week")}
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setFilters((prev) => ({ ...prev, date_range: "this_month" }));
            setCustomDateRange({ from: "", to: "" });
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2">This Month</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {getDateRangeDisplay("this_month")}
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setFilters((prev) => ({ ...prev, date_range: "current_year" }));
            setCustomDateRange({ from: "", to: "" });
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2">Current Year</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {getDateRangeDisplay("current_year")}
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setFilters((prev) => ({ ...prev, date_range: "financial_year" }));
            setCustomDateRange({ from: "", to: "" });
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2">Financial Year</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {getDateRangeDisplay("financial_year")}
            </Typography>
          </Box>
        </MenuItem>

        <Divider />

        {/* Custom Date Range */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDateRange.to || undefined,
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Summary Cards - Mobile: 2 per row, Desktop: 4 per row */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              p: 1,
              border: "2px solid #e0e0e0",
              borderRadius: 1.5,
              bgcolor: "#f5f5f5",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Opening Balance
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(0)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1,
              border: "2px solid #4caf50",
              borderRadius: 1.5,
              bgcolor: "#e8f5e9",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color="success.main"
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Total Debit
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="success.main"
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(summary?.total_debit || 0)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1,
              border: "2px solid #f44336",
              borderRadius: 1.5,
              bgcolor: "#ffebee",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color="error.main"
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Total Credit
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="error.main"
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(summary?.total_credit || 0)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1,
              border: `2px solid ${(summary?.balance || 0) >= 0 ? "#4caf50" : "#f44336"}`,
              borderRadius: 1.5,
              bgcolor: (summary?.balance || 0) >= 0 ? "#e8f5e9" : "#ffebee",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color={(summary?.balance || 0) >= 0 ? "success.main" : "error.main"}
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Closing Balance
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color={(summary?.balance || 0) >= 0 ? "success.main" : "error.main"}
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(summary?.balance || 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 🆕 Active Search Indicator */}
      {searchLedgerName && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Filtering by: ${searchLedgerName}`}
            onDelete={handleClearSearch}
            color="primary"
            size="small"
            sx={{ fontSize: "0.7rem" }}
          />
        </Box>
      )}

      {/* ✅ CONDITIONAL RENDERING: Mobile Cards | Desktop Table */}
      {isMobile ? (
        /* 📱 MOBILE VIEW - Expandable Dropdown Cards */
        <Box
          id="scrollableDiv"
          sx={{
            bgcolor: "background.paper",
          }}
        >
          <InfiniteScroll
            dataLength={ledgerData.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<h4 style={{ textAlign: "center", fontSize: "0.75rem" }}>Loading...</h4>}
            endMessage={
              <p style={{ textAlign: "center", padding: "10px", fontSize: "0.75rem" }}>
                No more vouchers!
              </p>
            }
          >
            {ledgerData.map((row, index) => (
              <Box key={index}>
                {/* Main Row - Always Visible (Click to Expand) */}
                <Box
                  onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:active": {
                      bgcolor: "#e3f2fd",
                    },
                    borderLeft: `4px solid ${row.type === "Receipt" ? "#4caf50" : "#f44336"}`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    {/* Date & Type */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        {formatOnlyDate(row.date)}
                      </Typography>
                      <Chip
                        label={row.type}
                        color={row.type === "Receipt" ? "success" : "error"}
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                          height: 18,
                        }}
                      />
                    </Box>

                    {/* Voucher Number */}
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#666",
                        mb: 0.3,
                      }}
                    >
                      {truncateText(row.number || "N/A", 25)}
                    </Typography>

                    {/* Amount (Debit or Credit) */}
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        color: row.debit > 0 ? "error.main" : "success.main",
                      }}
                    >
                      {row.debit > 0
                        ? `${formatCurrency(row.debit)} Dr`
                        : `${formatCurrency(row.credit)} Cr`}
                    </Typography>
                  </Box>

                  {/* Expand/Collapse Icon */}
                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedRow === index ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                {/* Expanded Details (Dropdown Content) */}
                <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                      borderLeft: `4px solid ${row.type === "Receipt" ? "#4caf50" : "#f44336"}`,
                    }}
                  >
                    {/* Description */}
                    <Box sx={{ mb: 0.8 }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        DESCRIPTION
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#333",
                        }}
                      >
                        {row.description || "N/A"}
                      </Typography>
                    </Box>

                    {/* Debit & Credit */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 0.8,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          DEBIT
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: row.debit > 0 ? "error.main" : "#999",
                          }}
                        >
                          {row.debit > 0 ? formatCurrency(row.debit) : "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          CREDIT
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: row.credit > 0 ? "success.main" : "#999",
                          }}
                        >
                          {row.credit > 0 ? formatCurrency(row.credit) : "-"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Balance */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        BALANCE
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          color: row.balance >= 0 ? "success.main" : "error.main",
                        }}
                      >
                        {formatCurrency(row.balance)}{" "}
                        {row.balance >= 0 ? "Cr" : "Dr"}
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>

                {/* Divider between cards */}
                <Divider />
              </Box>
            ))}
          </InfiniteScroll>
        </Box>
      ) : (
        /* 💻 DESKTOP VIEW - Original Table */
        <InfiniteScroll
          dataLength={ledgerData.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
          scrollableTarget="scrollableDiv"
          endMessage={<p style={{ textAlign: "center" }}>No more vouchers!</p>}
        >
        <TableContainer
          component={Paper}
          id="scrollableDiv"
          sx={{
            maxHeight: "73vh",
            overflow: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 1000,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Voucher No</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Debit</TableCell>
                  <TableCell>Credit</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Dr or Cr</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {ledgerData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "& td": {
                        paddingTop: "2px !important",
                        paddingBottom: "2px !important",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 120,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                      },
                      "&:hover": {
                        backgroundColor: "#e3f2fd !important",
                      },
                    }}
                  >
                    <TableCell>{formatOnlyDate(row.date)}</TableCell>

                    <TableCell>
                      <Chip
                        label={row.type}
                        color={row.type === "Receipt" ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Tooltip title={row.number || "-"} arrow>
                        <span>{truncateText(row.number || "-", 20)}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={row.description || "-"} arrow>
                        <span>{truncateText(row.description || "-", 20)}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      sx={{ color: row.debit > 0 ? "error.main" : "text.disabled" }}
                    >
                      {row.debit > 0 ? formatCurrency(row.debit) : "-"}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: row.credit > 0 ? "success.main" : "text.disabled",
                      }}
                    >
                      {row.credit > 0 ? formatCurrency(row.credit) : "-"}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: row.balance >= 0 ? "success.main" : "error.main",
                      }}
                    >
                      {formatCurrency(row.balance)}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.debit > 0 ? "Dr" : "Cr"}
                        color={row.debit > 0 ? "error" : "success"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </TableContainer>
          </InfiniteScroll>
      )}
    </Box>
  );
};

export default LedgerTable;